/**
 * Stage Progression Tab Component
 *
 * PE-769: Visual pipeline for Opportunity stages with click-to-advance functionality.
 * Shows stage history, duration tracking, and interactive stage advancement.
 */

import React, { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getStageHistory,
  getTimeInEachStage,
  logStageChange,
  getNextStage,
  formatDuration,
  getStageIndex,
  mapStageNameToId,
  OPPORTUNITY_STAGES,
  type StageChange,
} from '../../services/stageHistoryService';
import {
  Check,
  Clock,
  ChevronRight,
  TrendingUp,
  Trophy,
  XCircle,
  AlertCircle,
} from 'lucide-react';

interface StageProgressionTabProps {
  /** Opportunity ID */
  opportunityId: string;
  /** Current stage name or ID */
  currentStage: string;
  /** Callback when stage changes */
  onStageChange?: (newStage: string) => void;
}

export const StageProgressionTab: React.FC<StageProgressionTabProps> = ({
  opportunityId,
  currentStage,
  onStageChange,
}) => {
  const queryClient = useQueryClient();

  // Normalize stage to ID format
  const currentStageId = useMemo(
    () => mapStageNameToId(currentStage),
    [currentStage]
  );

  // Load stage history
  const { data: history = [], isLoading } = useQuery({
    queryKey: ['stageHistory', opportunityId],
    queryFn: async () => {
      await new Promise(resolve => setTimeout(resolve, 100));
      return getStageHistory(opportunityId);
    },
  });

  // Calculate time in each stage
  const timeByStage = useMemo(() => {
    return getTimeInEachStage(opportunityId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opportunityId, history]);

  // Stage change mutation
  const stageMutation = useMutation({
    mutationFn: async ({
      fromStage,
      toStage,
    }: {
      fromStage: string;
      toStage: string;
    }) => {
      await new Promise(resolve => setTimeout(resolve, 300));
      return logStageChange(
        opportunityId,
        fromStage,
        toStage,
        'user_current',
        'Current User'
      );
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['stageHistory', opportunityId] });
      queryClient.invalidateQueries({ queryKey: ['activity', opportunityId] });
      queryClient.invalidateQueries({ queryKey: ['opportunityRecords'] });
      if (onStageChange) {
        onStageChange(variables.toStage);
      }
    },
  });

  const handleStageClick = (stageId: string) => {
    // Don't allow clicking current stage
    if (stageId === currentStageId) return;

    // Don't allow changes if already closed
    const currentStageData = OPPORTUNITY_STAGES.find(s => s.id === currentStageId);
    if (currentStageData?.isClosedWon || currentStageData?.isClosedLost) {
      return;
    }

    const targetStage = OPPORTUNITY_STAGES.find(s => s.id === stageId);
    const confirmed = window.confirm(
      `Move this opportunity to "${targetStage?.label}"?`
    );

    if (confirmed) {
      stageMutation.mutate({
        fromStage: currentStageId,
        toStage: stageId,
      });
    }
  };

  const getStageColor = (
    color: string,
    isCurrent: boolean,
    isCompleted: boolean
  ): string => {
    const baseColors: Record<string, { current: string; completed: string; future: string }> = {
      gray: {
        current: 'bg-gray-500 border-gray-600 text-white',
        completed: 'bg-gray-100 border-gray-300 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
        future: 'bg-gray-50 border-gray-200 text-gray-400 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-500',
      },
      blue: {
        current: 'bg-blue-500 border-blue-600 text-white',
        completed: 'bg-blue-100 border-blue-300 text-blue-700 dark:bg-blue-900/30 dark:border-blue-700 dark:text-blue-400',
        future: 'bg-blue-50 border-blue-200 text-blue-400 dark:bg-blue-900/10 dark:border-blue-800 dark:text-blue-500',
      },
      yellow: {
        current: 'bg-yellow-500 border-yellow-600 text-white',
        completed: 'bg-yellow-100 border-yellow-300 text-yellow-700 dark:bg-yellow-900/30 dark:border-yellow-700 dark:text-yellow-400',
        future: 'bg-yellow-50 border-yellow-200 text-yellow-500 dark:bg-yellow-900/10 dark:border-yellow-800 dark:text-yellow-500',
      },
      orange: {
        current: 'bg-orange-500 border-orange-600 text-white',
        completed: 'bg-orange-100 border-orange-300 text-orange-700 dark:bg-orange-900/30 dark:border-orange-700 dark:text-orange-400',
        future: 'bg-orange-50 border-orange-200 text-orange-400 dark:bg-orange-900/10 dark:border-orange-800 dark:text-orange-500',
      },
      green: {
        current: 'bg-green-500 border-green-600 text-white',
        completed: 'bg-green-100 border-green-300 text-green-700 dark:bg-green-900/30 dark:border-green-700 dark:text-green-400',
        future: 'bg-green-50 border-green-200 text-green-400 dark:bg-green-900/10 dark:border-green-800 dark:text-green-500',
      },
      red: {
        current: 'bg-red-500 border-red-600 text-white',
        completed: 'bg-red-100 border-red-300 text-red-700 dark:bg-red-900/30 dark:border-red-700 dark:text-red-400',
        future: 'bg-red-50 border-red-200 text-red-400 dark:bg-red-900/10 dark:border-red-800 dark:text-red-500',
      },
    };

    const colorSet = baseColors[color] || baseColors.gray;

    if (isCurrent) return colorSet.current;
    if (isCompleted) return colorSet.completed;
    return colorSet.future;
  };

  const nextStage = getNextStage(currentStageId);
  const currentStageIndex = getStageIndex(currentStageId);
  const currentStageData = OPPORTUNITY_STAGES.find(s => s.id === currentStageId);
  const isClosed = currentStageData?.isClosedWon || currentStageData?.isClosedLost;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-6 w-40 bg-gray-200 dark:bg-gray-700 rounded mb-6"></div>
          <div className="flex gap-4">
            {[1, 2, 3, 4, 5].map(i => (
              <div
                key={i}
                className="w-32 h-24 bg-gray-200 dark:bg-gray-700 rounded-lg"
              ></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          Sales Pipeline
        </h3>

        {nextStage && !isClosed && (
          <button
            onClick={() => handleStageClick(nextStage.id)}
            disabled={stageMutation.isPending}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <TrendingUp className="h-4 w-4" />
            <span>Advance to {nextStage.label}</span>
          </button>
        )}

        {isClosed && (
          <div
            className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
              currentStageData?.isClosedWon
                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
            }`}
          >
            {currentStageData?.isClosedWon ? (
              <Trophy className="h-4 w-4" />
            ) : (
              <XCircle className="h-4 w-4" />
            )}
            <span className="font-medium">{currentStageData?.label}</span>
          </div>
        )}
      </div>

      {/* Visual Pipeline */}
      <div className="bg-white dark:bg-[#1e293b] rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center gap-2 overflow-x-auto pb-2">
          {OPPORTUNITY_STAGES.filter(s => !s.isClosedLost).map((stage, index) => {
            const isCurrent = stage.id === currentStageId;
            const isCompleted = index < currentStageIndex && currentStageIndex !== -1;
            const duration = timeByStage[stage.id];
            const isClickable = !isClosed && !isCurrent;

            return (
              <React.Fragment key={stage.id}>
                {index > 0 && (
                  <ChevronRight className="h-5 w-5 text-gray-400 flex-shrink-0" />
                )}

                <button
                  onClick={() => isClickable && handleStageClick(stage.id)}
                  disabled={!isClickable || stageMutation.isPending}
                  className={`
                    flex-shrink-0 relative px-5 py-4 rounded-lg border-2 transition-all min-w-[130px]
                    ${getStageColor(stage.color, isCurrent, isCompleted)}
                    ${isClickable ? 'hover:ring-2 hover:ring-blue-400 cursor-pointer' : 'cursor-default'}
                    ${isCurrent ? 'ring-2 ring-blue-500 ring-offset-2 dark:ring-offset-gray-900' : ''}
                  `}
                >
                  <div className="flex flex-col items-center gap-2">
                    {/* Icon */}
                    <div className="h-6 flex items-center justify-center">
                      {isCompleted && !isCurrent && (
                        <Check className="h-5 w-5" />
                      )}
                      {stage.isClosedWon && isCurrent && (
                        <Trophy className="h-5 w-5" />
                      )}
                      {isCurrent && !stage.isClosedWon && (
                        <Clock className="h-5 w-5" />
                      )}
                      {!isCompleted && !isCurrent && (
                        <div className="h-2 w-2 rounded-full bg-current opacity-50" />
                      )}
                    </div>

                    {/* Stage Name */}
                    <span className="font-semibold text-sm text-center whitespace-nowrap">
                      {stage.label}
                    </span>

                    {/* Probability */}
                    <span className="text-xs opacity-75">
                      {stage.probability}% likely
                    </span>

                    {/* Duration */}
                    {duration > 0 && (
                      <span className="text-xs opacity-75">
                        {formatDuration(duration)}
                      </span>
                    )}
                  </div>

                  {/* Current indicator */}
                  {isCurrent && (
                    <div className="absolute -top-1 -right-1 w-3 h-3 bg-blue-500 rounded-full border-2 border-white dark:border-gray-900" />
                  )}
                </button>
              </React.Fragment>
            );
          })}
        </div>

        {/* Closed Lost option */}
        {!isClosed && (
          <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700 flex justify-end">
            <button
              onClick={() => handleStageClick('closed_lost')}
              disabled={stageMutation.isPending}
              className="flex items-center gap-2 px-4 py-2 border-2 border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-50"
            >
              <XCircle className="h-4 w-4" />
              <span className="text-sm font-medium">Mark as Closed Lost</span>
            </button>
          </div>
        )}
      </div>

      {/* Stage History Timeline */}
      <div className="bg-white dark:bg-[#1e293b] rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <h4 className="font-semibold text-gray-900 dark:text-white mb-4">
          Stage History
        </h4>

        {history.length === 0 ? (
          <div className="text-center py-8">
            <Clock className="h-12 w-12 text-gray-400 mx-auto mb-3" />
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No stage changes recorded yet
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
              Stage changes will appear here as the opportunity progresses
            </p>
          </div>
        ) : (
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-4 top-2 bottom-2 w-0.5 bg-gray-200 dark:bg-gray-700" />

            {/* Timeline entries */}
            <div className="space-y-4">
              {[...history].reverse().map((change: StageChange) => {
                const fromStageData = OPPORTUNITY_STAGES.find(
                  s => s.id === change.fromStage || s.name === change.fromStage
                );
                const toStageData = OPPORTUNITY_STAGES.find(
                  s => s.id === change.toStage || s.name === change.toStage
                );

                return (
                  <div key={change.id} className="relative pl-10">
                    {/* Timeline dot */}
                    <div
                      className={`absolute left-2.5 top-2 w-3 h-3 rounded-full border-2 border-white dark:border-gray-900 ${
                        toStageData?.isClosedWon
                          ? 'bg-green-500'
                          : toStageData?.isClosedLost
                          ? 'bg-red-500'
                          : 'bg-blue-500'
                      }`}
                    />

                    <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                      <div className="flex items-center gap-2 text-sm">
                        <span className="font-medium text-gray-700 dark:text-gray-300">
                          {fromStageData?.label || change.fromStage}
                        </span>
                        <ChevronRight className="h-4 w-4 text-gray-400" />
                        <span
                          className={`font-medium ${
                            toStageData?.isClosedWon
                              ? 'text-green-600 dark:text-green-400'
                              : toStageData?.isClosedLost
                              ? 'text-red-600 dark:text-red-400'
                              : 'text-blue-600 dark:text-blue-400'
                          }`}
                        >
                          {toStageData?.label || change.toStage}
                        </span>
                      </div>

                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-500 dark:text-gray-400">
                        <span>
                          {new Date(change.timestamp).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                          })}
                        </span>
                        <span>by {change.userName}</span>
                        {change.duration > 0 && (
                          <>
                            <span>•</span>
                            <span>{formatDuration(change.duration)} in previous stage</span>
                          </>
                        )}
                      </div>

                      {change.notes && (
                        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400 italic">
                          "{change.notes}"
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Mutation loading indicator */}
      {stageMutation.isPending && (
        <div className="fixed inset-0 bg-black/20 dark:bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-xl flex items-center gap-3">
            <div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full" />
            <span className="text-gray-900 dark:text-white">Updating stage...</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default StageProgressionTab;
